import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { kundenUrl, type ImportStatus, type ImportVorschau } from './KundeDaten';
import { fehlerText } from './tarifHinweis';
import type { Overview } from './types';
import './kunden.css';

const BILLTORO = isBilltoro();
const MAX_MB = 2;
// sum: Text neben der Zahl in der Kachel („1 Kunde wird angelegt“, „3 Lieferanten werden angelegt“)
const STATUS: Record<ImportStatus, { label: string; cls: string; sum: (n: number) => string }> = {
  // Billtoro mit Lieferanten daneben: „Kunden“ dazu; die Kanzlei-App legt nur Kunden an
  neu: {
    label: 'Neu',
    cls: 'b-ok',
    sum: (n) => (BILLTORO ? (n === 1 ? 'Kunde wird angelegt' : 'Kunden werden angelegt') : n === 1 ? 'wird angelegt' : 'werden angelegt'),
  },
  // Billtoro: in die Lieferantenliste
  lieferant: { label: 'Lieferant', cls: 'b-ok', sum: (n) => (n === 1 ? 'Lieferant wird angelegt' : 'Lieferanten werden angelegt') },
  doppelt: { label: 'Schon da', cls: 'b-none', sum: () => 'schon in der Liste' },
  fehler: { label: 'Geht nicht', cls: 'b-bad', sum: () => 'mit Fehler' },
  // Kanzlei-App: Lieferanten bleiben draußen
  uebersprungen: { label: 'Lieferant', cls: 'b-none', sum: (n) => (n === 1 ? 'Lieferant' : 'Lieferanten') },
};
const ZEIGE = 100;
const text = (v: unknown) => (typeof v === 'string' ? v : null);
const kundenN = (n: number) => (n === 1 ? '1 Kunde' : `${n} Kunden`);
const lieferantenN = (n: number) => (n === 1 ? '1 Lieferant' : `${n} Lieferanten`);

// Kunden importieren (/rechnung/kunden/import): CSV oder Excel aus Lexware Office, sevDesk oder einer eigenen Liste.
// Der Server liest die Datei (ohne KI) und zeigt erst, was angelegt würde; angelegt wird erst nach dem Bestätigen.
// Billtoro: Lieferanten der Datei kommen in die Lieferantenliste. Aus der Lieferantenliste geöffnet: zurück dorthin,
// und jede Zeile wird ein Lieferant, außer Kontaktart oder Kategorie sagen „Kunde“ (?als=lieferanten). Billtoro nennt
// Lexware nicht.
export function KundenImport({ data, onDone }: { data: Overview; onDone(): Promise<void> }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const vonLieferanten = BILLTORO && (location.state as { von?: string } | null)?.von === 'lieferanten';
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [v, setV] = useState<ImportVorschau | null>(null);
  const [datei, setDatei] = useState('');
  const [zeige, setZeige] = useState<ImportStatus | 'alle'>('alle');
  const [mehr, setMehr] = useState(ZEIGE);

  async function lesen(f: File) {
    setError(null);
    setV(null);
    if (f.size > MAX_MB * 1024 * 1024) return setError(`Die Datei ist zu groß (höchstens ${MAX_MB} MB).`);
    setBusy(true);
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const r = await api.post<ImportVorschau>(`${kundenUrl(data.firm.id)}/import${vonLieferanten ? '?als=lieferanten' : ''}`, fd);
      setV(r);
      setDatei(f.name);
      setZeige('alle');
      setMehr(ZEIGE);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function anlegen() {
    if (!v) return;
    setSaving(true);
    setError(null);
    try {
      const kunden = v.zeilen.filter((z) => z.status === 'neu' && z.kunde).map((z) => z.kunde);
      const lieferanten = v.zeilen.filter((z) => z.status === 'lieferant' && z.lieferant).map((z) => z.lieferant);
      const r = await api.post<{ angelegt: number; doppelt: number; lieferanten?: { angelegt: number; doppelt: number } }>(
        `${kundenUrl(data.firm.id)}/import/anlegen`,
        BILLTORO ? { kunden, lieferanten, quelle: v.format } : { kunden, quelle: v.format },
      );
      await onDone();
      // „2 Kunden und 1 Lieferant angelegt“
      const l = r.lieferanten?.angelegt ?? 0;
      const doppelt = r.doppelt + (r.lieferanten?.doppelt ?? 0);
      const angelegt = [r.angelegt || !l ? kundenN(r.angelegt) : null, l ? lieferantenN(l) : null].filter(Boolean).join(' und ');
      toast(`${angelegt} angelegt${doppelt ? `, ${doppelt} gab es inzwischen schon` : ''}`);
      navigate(l && !r.angelegt ? '/rechnung/lieferanten' : '/rechnung/kunden');
    } catch (err) {
      setError(fehlerText(err));
      setSaving(false);
    }
  }

  const waehlen = () => input.current?.click();
  const dateiFeld = (
    <input
      ref={input}
      type="file"
      hidden
      aria-label={BILLTORO ? 'Datei mit Kunden und Lieferanten' : 'Datei mit Kunden'}
      accept=".csv,.txt,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) void lesen(f);
      }}
    />
  );

  if (!v) {
    return (
      <>
        <Link to={vonLieferanten ? '/rechnung/lieferanten' : '/rechnung/kunden'} className="kd-back">
          {vonLieferanten ? '← Alle Lieferanten' : '← Alle Kunden'}
        </Link>
        <div className="rt-head">
          <div>
            <h1>{BILLTORO ? 'Kunden und Lieferanten importieren' : 'Kunden importieren'}</h1>
            <p className="muted">
              {BILLTORO ? 'Aus Ihrem bisherigen Rechnungsprogramm oder Ihrer eigenen Excel-Liste' : 'Aus Lexware Office, sevDesk oder Ihrer eigenen Excel-Liste'}
            </p>
          </div>
        </div>
        <div className="kd-imp">
          <div className="rt-card kd-imp-card">
            <h2>Datei auswählen</h2>
            <p>
              <b>CSV oder Excel (.xlsx)</b>, höchstens {MAX_MB} MB. Oben eine Zeile mit Überschriften wie Name oder Firma, Straße, PLZ, Ort,
              E-Mail. Sie sehen erst eine Vorschau, angelegt wird nach Ihrem OK.
            </p>
            <button type="button" className="btn primary" disabled={busy} onClick={waehlen}>
              {busy ? 'Datei wird gelesen …' : 'Datei auswählen'}
            </button>
            {dateiFeld}
            {error && <div className="notice bad">{error}</div>}
          </div>
          <div className="rt-card kd-imp-help">
            <h2>So bekommen Sie die Datei</h2>
            <dl>
              {!BILLTORO && (
                <div>
                  <dt>Lexware Office</dt>
                  <dd>Bei den Kontakten exportieren, als CSV</dd>
                </div>
              )}
              <div>
                <dt>sevDesk</dt>
                <dd>Bei den Kontakten exportieren, als CSV</dd>
              </div>
              {BILLTORO && (
                <div>
                  <dt>Anderes Rechnungsprogramm</dt>
                  <dd>Bei den Kontakten exportieren, als CSV</dd>
                </div>
              )}
              <div>
                <dt>Excel</dt>
                <dd>Ihre Liste wie sie ist, als .xlsx oder CSV</dd>
              </div>
            </dl>
            <p className="muted">
              {!BILLTORO
                ? 'Kundennummern aus der Datei werden übernommen, wenn sie frei sind. Lieferanten bleiben draußen.'
                : vonLieferanten
                  ? 'Jede Zeile wird ein Lieferant, außer in einer Spalte Kontaktart oder Kategorie steht „Kunde“. Kundennummern und Lieferantennummern aus der Datei werden übernommen, wenn sie frei sind.'
                  : 'Lieferanten erkennt Billtoro an „Lieferant“ in einer Spalte Kontaktart oder Kategorie und bringt sie in die Lieferantenliste. Hat die Datei eine Spalte Lieferantennummer und keine Spalte Kundennummer, wird jede Zeile ein Lieferant, außer in Kontaktart oder Kategorie steht „Kunde“. Eine Liste nur mit Lieferanten importieren Sie über die Lieferantenliste. Kundennummern und Lieferantennummern aus der Datei werden übernommen, wenn sie frei sind.'}
            </p>
          </div>
        </div>
      </>
    );
  }

  const shown = v.zeilen.filter((z) => zeige === 'alle' || z.status === zeige);
  const neu = v.anzahl.neu ?? 0;
  // Billtoro: Lieferanten zählen mit („3 Kontakte anlegen“, wenn beides vorkommt)
  const lief = v.anzahl.lieferant ?? 0;
  const knopf =
    neu && lief
      ? `${neu + lief} Kontakte anlegen`
      : lief
        ? `${lief === 1 ? '1 Lieferanten' : `${lief} Lieferanten`} anlegen`
        : neu
          ? `${neu === 1 ? '1 Kunden' : `${neu} Kunden`} anlegen`
          : BILLTORO
            ? 'Kein neuer Kontakt'
            : 'Kein neuer Kunde';
  const spalten = v.spalten.map((s) => [s.spalte, s.feld] as const);
  const anlegenKnopf = (
    <button type="button" className="btn primary" disabled={saving || neu + lief === 0} onClick={() => void anlegen()}>
      {saving ? 'Wird angelegt …' : knopf}
    </button>
  );
  return (
    <>
      <button type="button" className="kd-back linkbtn" onClick={() => setV(null)}>
        ← Andere Datei
      </button>
      <div className="rt-head kd-imp-head">
        <div>
          <h1>Vorschau</h1>
          <p className="muted kd-datei">
            {datei} · erkannt: {v.format}
          </p>
        </div>
        <div className="rt-head-act">{anlegenKnopf}</div>
      </div>

      <div className="kd-imp-sum" role="group" aria-label="Zeilen zeigen">
        {(['neu', 'lieferant', 'doppelt', 'fehler', 'uebersprungen'] as const).map((s) =>
          v.anzahl[s] || s === 'neu' ? (
            <button
              key={s}
              type="button"
              className={`kd-imp-n ${s}${zeige === s ? ' on' : ''}`}
              aria-pressed={zeige === s}
              onClick={() => {
                setZeige(zeige === s ? 'alle' : s);
                setMehr(ZEIGE);
              }}
            >
              <b className="num">{v.anzahl[s] ?? 0}</b>
              <span>{STATUS[s].sum(v.anzahl[s] ?? 0)}</span>
            </button>
          ) : null,
        )}
      </div>

      <details className="kd-spalten">
        <summary>Erkannte Spalten ({spalten.length})</summary>
        <ul>
          {spalten.map(([spalte, feld]) => (
            <li key={spalte}>
              <span>{spalte}</span> → <b>{feld}</b>
            </li>
          ))}
        </ul>
      </details>

      <ul className="kd-imp-list">
        {shown.slice(0, mehr).map((z) => (
          <li key={z.zeile} className={z.status}>
            <div className="kd-imp-top">
              <span className={`badge ${STATUS[z.status].cls}`}>{STATUS[z.status].label}</span>
              <b className="kd-imp-name">{z.name || '(ohne Name)'}</b>
              {z.nummer != null && <span className="kd-nr num">{z.nummer}</span>}
            </div>
            <div className="kd-imp-sub">
              {[`Zeile ${z.zeile}`, text((z.kunde ?? z.lieferant)?.extra), text((z.kunde ?? z.lieferant)?.street), z.ort.trim(), z.email]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {z.fehler && <div className={z.status === 'fehler' ? 'kd-imp-err' : 'kd-imp-sub'}>{z.fehler}</div>}
            {z.hinweise.length > 0 && (
              <ul className="kd-imp-hint">
                {z.hinweise.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {shown.length > mehr && (
        <p className="kd-mehr">
          <button type="button" className="btn" onClick={() => setMehr(mehr + ZEIGE * 5)}>
            Weitere zeigen ({shown.length - mehr})
          </button>
        </p>
      )}

      {error && <div className="notice bad">{error}</div>}
      <div className="kd-imp-act">
        <button type="button" className="btn" onClick={() => setV(null)} disabled={saving}>
          Andere Datei
        </button>
        {anlegenKnopf}
      </div>
    </>
  );
}
